import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertOctagon, ChevronLeft, Printer, Share2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { T } from "@/constants/testIds";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [err, setErr] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState("");
  const [reportEmail, setReportEmail] = useState("");

  useEffect(() => {
    api
      .get(`/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setErr(true));
  }, [slug]);

  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: article.title, url: window.location.href });
      else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Link copied");
      }
    } catch {
      /* ignore user cancel */
    }
  };

  const submitReport = async (e) => {
    e.preventDefault();
    try {
      await api.post("/forms/correction", {
        content_type: "article",
        content_id: article.id,
        content_slug: article.slug,
        reporter_email: reportEmail || null,
        description: reportText,
      });
      toast.success("Thank you. An editor will review your note.");
      setReportOpen(false);
      setReportText("");
      setReportEmail("");
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || er.message);
    }
  };

  if (err) {
    return (
      <div className="container-reading py-24 text-center">
        <h1 className="heading-2">Article not found.</h1>
        <Link to="/articles" className="mt-6 btn-primary inline-flex">All Articles</Link>
      </div>
    );
  }
  if (!article) return <div className="container-reading py-24 text-ink-muted">Loading…</div>;

  return (
    <article>
      <header className="bg-subtle border-b border-border py-16 sm:py-20">
        <div className="container-reading">
          <Link to="/articles" className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-forest">
            <ChevronLeft className="h-4 w-4" /> Articles
          </Link>
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-ink-muted mt-6">
            <span className="text-forest">{article.category}</span>
            <span className="h-1 w-1 rounded-full bg-ink-muted/40" />
            <span>{article.reading_time_min} min read</span>
            {article.reviewer_name && (
              <>
                <span className="h-1 w-1 rounded-full bg-ink-muted/40" />
                <span>Reviewed by {article.reviewer_name}</span>
              </>
            )}
          </div>
          <h1 data-testid={T.articleTitle} className="heading-1 mt-4">
            {article.title}
          </h1>
          <p className="lead mt-6">{article.summary}</p>
          <div className="mt-8 flex items-center gap-4 text-sm text-ink-muted">
            <span>By {article.author_name}</span>
            {article.published_at && <span>· {new Date(article.published_at).toLocaleDateString()}</span>}
          </div>
        </div>
      </header>

      <div className="container-reading py-16">
        {article.primary_scripture && (
          <div data-testid={T.articleContextBox} className="my-2 mb-10 border-l-2 border-gold bg-subtle p-6">
            <p className="eyebrow">Primary Scripture</p>
            <p className="mt-2 text-ink font-serif text-xl italic">{article.primary_scripture}</p>
            {article.context_note && <p className="mt-3 body-copy text-ink-muted">{article.context_note}</p>}
          </div>
        )}

        <div
          data-testid={T.articleBody}
          className="prose-editorial"
          dangerouslySetInnerHTML={{ __html: article.body }}
        />

        {article.does_not_mean && (
          <div data-testid={T.articleDoesNotMean} className="my-10 border border-terracotta/40 bg-terracotta/5 p-6">
            <p className="eyebrow text-terracotta-dark">What this does not mean</p>
            <p className="mt-3 text-ink body-copy">{article.does_not_mean}</p>
          </div>
        )}

        {article.application && (
          <div className="my-10 border-l-2 border-forest pl-6">
            <p className="eyebrow text-forest">Application</p>
            <p className="mt-2 body-copy">{article.application}</p>
          </div>
        )}

        {article.reflection_questions?.length > 0 && (
          <div data-testid={T.articleReflection} className="my-10 paper-card p-8 bg-subtle">
            <p className="eyebrow">Reflection questions</p>
            <ol className="mt-4 space-y-3 list-decimal pl-6 text-ink-soft">
              {article.reflection_questions.map((q, i) => (
                <li key={i}>{q}</li>
              ))}
            </ol>
          </div>
        )}

        {article.supporting_scriptures?.length > 0 && (
          <div className="my-10">
            <p className="eyebrow">Related passages</p>
            <p className="mt-2 body-copy">{article.supporting_scriptures.join(" · ")}</p>
          </div>
        )}

        <div className="mt-14 pt-8 border-t border-border flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xs text-ink-muted">
            {article.editor_note && <span>{article.editor_note}</span>}
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => window.print()} className="text-sm text-ink-muted hover:text-forest inline-flex items-center gap-1">
              <Printer className="h-3.5 w-3.5" /> Print
            </button>
            <button onClick={share} className="text-sm text-ink-muted hover:text-forest inline-flex items-center gap-1">
              <Share2 className="h-3.5 w-3.5" /> Share
            </button>
            <button
              data-testid={T.articleReportBtn}
              onClick={() => setReportOpen((v) => !v)}
              className="text-sm text-ink-muted hover:text-terracotta-dark inline-flex items-center gap-1"
            >
              <AlertOctagon className="h-3.5 w-3.5" /> Report a correction
            </button>
          </div>
        </div>

        {reportOpen && (
          <form onSubmit={submitReport} className="mt-6 paper-card p-6">
            <p className="eyebrow">Suggest a correction</p>
            <input
              type="email"
              placeholder="Your email (optional)"
              value={reportEmail}
              onChange={(e) => setReportEmail(e.target.value)}
              className="mt-3 w-full border border-border bg-paper px-3 py-2 text-sm focus:outline-none focus:border-ink"
            />
            <textarea
              required
              rows={4}
              value={reportText}
              onChange={(e) => setReportText(e.target.value)}
              placeholder="What should we look at?"
              className="mt-3 w-full border border-border bg-paper px-3 py-2 text-sm focus:outline-none focus:border-ink"
            />
            <div className="mt-3 flex gap-3">
              <button className="btn-primary" type="submit">Send</button>
              <button type="button" className="text-sm text-ink-muted underline" onClick={() => setReportOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </article>
  );
}
